/**
 * Utility functions for normalizing and comparing tail numbers.
 * Handles international tail numbers that may include hyphens (e.g., "C-FWAP").
 */

/**
 * Removes hyphens from a tail number for comparison purposes.
 * @param tailNumber - The tail number to normalize (e.g., "C-FWAP" or "N12345")
 * @returns The tail number without hyphens (e.g., "CFWAP" or "N12345")
 */
export function stripHyphens(tailNumber: string): string {
  return tailNumber.replace(/-/g, '');
}

/**
 * Compares two tail numbers ignoring hyphens.
 * @param a - First tail number
 * @param b - Second tail number
 * @returns true if the tail numbers match when hyphens are removed
 */
export function tailNumbersMatch(a: string, b: string): boolean {
  return stripHyphens(a.toUpperCase()) === stripHyphens(b.toUpperCase());
}

/**
 * Checks if a tail number starts with a given prefix, ignoring hyphens.
 * @param tailNumber - The tail number to check
 * @param prefix - The prefix to check for
 * @returns true if the tail number starts with the prefix (ignoring hyphens)
 */
export function tailNumberStartsWith(tailNumber: string, prefix: string): boolean {
  return stripHyphens(tailNumber.toUpperCase()).startsWith(stripHyphens(prefix.toUpperCase()));
}

/**
 * Checks if a tail number contains a given substring, ignoring hyphens.
 * @param tailNumber - The tail number to check
 * @param substring - The substring to check for
 * @returns true if the tail number contains the substring (ignoring hyphens)
 */
export function tailNumberIncludes(tailNumber: string, substring: string): boolean {
  return stripHyphens(tailNumber.toUpperCase()).includes(stripHyphens(substring.toUpperCase()));
}
